import { httpsCallable } from "firebase/functions";
import { functions } from "../firebase/config";

/** Features the PWA reports to aiProxy. Usage is grouped by these in AI settings. */
export type AiFeature = "chat" | "insights" | "sessionBuilder";

export type AiPart =
  | { text: string }
  | { inlineData: { mimeType: string; data: string } };

export interface AiContent {
  role: "user" | "model";
  parts: AiPart[];
}

export interface AiRequest {
  feature: AiFeature;
  model: string;
  contents: AiContent[];
  systemInstruction?: string;
  generationConfig?: {
    temperature?: number;
    maxOutputTokens?: number;
    json?: boolean;
    thinkingBudget?: number;
  };
}

export interface AiAllowance {
  context: "athlete";
  month: string;
  limit: number;
  used: number;
  remaining: number;
}

export interface AiResponse {
  text: string;
  finishReason: string | null;
  allowance: AiAllowance;
}

/** Fallback monthly tokens per athlete, used before the real limit (set in PeakAdmin) loads. */
export const DEFAULT_MONTHLY_TOKEN_ALLOWANCE = 250_000;

/** Allowance months follow UTC because the server records usage by UTC month, e.g. "2026-10". */
export const usageMonthKey = (date = new Date()) =>
  `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, "0")}`;

export const monthlyAllowanceResetLabel = () => {
  const now = new Date();
  return new Date(
    Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1),
  ).toLocaleDateString("en-AU", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

/** Why aiProxy refused a request for the allowance, from the error's details. */
export type AllowanceRefusalReason = "exhausted" | "insufficient" | "disabled";

export const allowanceRefusalMessage = (reason?: AllowanceRefusalReason) => {
  if (reason === "disabled") return "AI isn't available for your account right now.";
  if (reason === "insufficient") {
    return `This request needs more of this month's AI token allowance than you have left. It resets ${monthlyAllowanceResetLabel()}.`;
  }
  return `You've used this month's AI token allowance. It resets ${monthlyAllowanceResetLabel()}.`;
};

export class AllowanceExceededError extends Error {
  readonly reason?: AllowanceRefusalReason;

  constructor(reason?: AllowanceRefusalReason) {
    super(allowanceRefusalMessage(reason));
    this.name = "AllowanceExceededError";
    this.reason = reason;
  }
}

/** aiProxy accepts plain MIME types, e.g. "audio/webm" rather than "audio/webm;codecs=opus". */
const MIME_ALIASES: Record<string, string> = {
  "audio/x-m4a": "audio/mp4",
  "audio/m4a": "audio/mp4",
  "audio/mp3": "audio/mpeg",
  "audio/x-wav": "audio/wav",
  "audio/wave": "audio/wav",
  "audio/x-aac": "audio/aac",
  "image/jpg": "image/jpeg",
};

export const normaliseMimeType = (mimeType: string) => {
  const plain = mimeType.split(";")[0].trim().toLowerCase();
  return MIME_ALIASES[plain] ?? plain;
};

const AI_TIMEOUT_MS = 300_000;

/**
 * Runs one model call through the aiProxy function, which checks the athlete's
 * monthly allowance and records the usage on the server.
 */
export async function callAi(request: AiRequest): Promise<AiResponse> {
  const aiProxy = httpsCallable<Record<string, unknown>, AiResponse>(
    functions,
    "aiProxy",
    { timeout: AI_TIMEOUT_MS },
  );
  try {
    const { data } = await aiProxy({ app: "pwa", ...request });
    return data;
  } catch (error: any) {
    if (error?.code === "functions/resource-exhausted") {
      throw new AllowanceExceededError(error?.details?.reason as AllowanceRefusalReason | undefined);
    }
    throw error;
  }
}

/** The signed-in athlete's allowance for the current month, shared across the athlete apps. */
export async function getAiAllowance(): Promise<AiAllowance> {
  const getAllowance = httpsCallable<{ app: "pwa" }, AiAllowance>(
    functions,
    "getAiAllowance",
  );
  const { data } = await getAllowance({ app: "pwa" });
  return data;
}
